import { PracticeCropWithDefaults } from "utils";

import type { CropYear, ScenarioMainCropOption } from "pages/app/scenario-analysis/types/scenario";

/** Main-crop select options: every enabled, model-ready Silver main crop, plus the crops the rotation already uses. */
export const buildSilverModelMainCropSelectOptions = (
  crops: PracticeCropWithDefaults[] | undefined,
  selectedCropIds: readonly string[]
): ScenarioMainCropOption[] => {
  if (!crops?.length) return [];

  const cropById = new Map(crops.map((c) => [c.id, c]));

  const selectedIdSet = new Set(selectedCropIds.filter((id) => id?.trim()));
  const passesFilter = (c: PracticeCropWithDefaults) => c.enabled && c.isMainCrop && c.isSilverModelReady === true && c.isModelReady;

  const byId = new Map<string, PracticeCropWithDefaults>();
  for (const c of crops) {
    if (passesFilter(c)) byId.set(c.id, c);
  }

  for (const id of selectedIdSet) {
    const row = cropById.get(id);
    if (row && !byId.has(id)) byId.set(id, row);
  }

  return [...byId.values()]
    .filter((c) => c.name)
    .map((c) => ({ value: c.id, label: c.name, residueEnabled: c.residue, residueLeftByDefault: c.residueLeftByDefault }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Display name by crop id. */
export const buildMainCropLabelLookup = (crops: Array<{ id: string; name: string }> | undefined): Record<string, string> => {
  if (!crops?.length) return {};
  return Object.fromEntries(crops.map((c) => [c.id, c.name]));
};

/** Crop ids a rotation already uses, so the select keeps them even when they are not Silver-model crops. */
export const getSelectedMainCropIds = (years: Array<Pick<CropYear, "crop">>): string[] =>
  years.map((year) => year.crop).filter((id): id is string => Boolean(id?.trim()));
